import { useEffect, useRef, useCallback } from 'react';
import { supabase } from '../config/supabase';
import type { RealtimeChannel } from '@supabase/supabase-js';
import type { BattleRoomState, BattlePlayerState, BattleRoundReveal } from '../types/battleRush';

interface UseBattleRoomOptions {
  roomId: string | null;
  onRoomUpdate: (payload: Partial<BattleRoomState>) => void;
  onPlayersUpdate: (players: BattlePlayerState[]) => void;
}

/**
 * Subscribes to Supabase Realtime changes on battle_rooms and battle_players
 * for the given room. Calls back with parsed updates.
 */
export function useBattleRoom({ roomId, onRoomUpdate, onPlayersUpdate }: UseBattleRoomOptions) {
  const channelRef = useRef<RealtimeChannel | null>(null);
  const onRoomUpdateRef = useRef(onRoomUpdate);
  const onPlayersUpdateRef = useRef(onPlayersUpdate);

  // Keep refs in sync to avoid re-subscribing on callback changes
  useEffect(() => { onRoomUpdateRef.current = onRoomUpdate; }, [onRoomUpdate]);
  useEffect(() => { onPlayersUpdateRef.current = onPlayersUpdate; }, [onPlayersUpdate]);

  const fetchRoomState = useCallback(async (id: string) => {
    const { data: room } = await supabase
      .from('battle_rooms')
      .select('status, current_question_index, current_question, round_status, round_reveal, round_started_at, winner_user_id, host_user_id')
      .eq('id', id)
      .single();

    if (room) {
      onRoomUpdateRef.current({
        status: room.status as BattleRoomState['status'],
        currentQuestionIndex: room.current_question_index,
        currentQuestion: room.current_question as BattleRoomState['currentQuestion'],
        roundStatus: room.round_status as BattleRoomState['roundStatus'],
        roundReveal: room.round_reveal as BattleRoundReveal | null,
        roundStartedAt: room.round_started_at,
        winnerUserId: room.winner_user_id,
        hostUserId: room.host_user_id,
      });
    }
  }, []);

  const fetchPlayersState = useCallback(async (id: string) => {
    const { data } = await supabase
      .from('battle_players')
      .select('user_id, username, lives, score, has_answered, is_eliminated, is_ready')
      .eq('room_id', id)
      .order('joined_at', { ascending: true });

    if (data) {
      onPlayersUpdateRef.current(
        data.map((p: any) => ({
          userId: p.user_id,
          username: p.username,
          lives: p.lives,
          score: p.score,
          hasAnswered: p.has_answered,
          isEliminated: p.is_eliminated,
          isReady: p.is_ready,
        }))
      );
    }
  }, []);

  useEffect(() => {
    if (!roomId) return;

    const channel = supabase
      .channel(`battle-${roomId}`)
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'battle_rooms', filter: `id=eq.${roomId}` },
        (payload) => {
          const row = payload.new as any;
          onRoomUpdateRef.current({
            status: row.status,
            currentQuestionIndex: row.current_question_index,
            currentQuestion: row.current_question,
            roundStatus: row.round_status,
            roundReveal: row.round_reveal as BattleRoundReveal | null,
            roundStartedAt: row.round_started_at,
            winnerUserId: row.winner_user_id,
            hostUserId: row.host_user_id,
          });
        }
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'battle_players', filter: `room_id=eq.${roomId}` },
        () => { fetchPlayersState(roomId); }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          // Fetch current state to catch any events missed during subscription setup
          fetchRoomState(roomId);
          fetchPlayersState(roomId);
        }
      });

    channelRef.current = channel;

    return () => {
      supabase.removeChannel(channel);
      channelRef.current = null;
    };
  }, [roomId, fetchRoomState, fetchPlayersState]);

  const unsubscribe = useCallback(() => {
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }
  }, []);

  return { unsubscribe };
}
